import {
  Bar, BarChart, CartesianGrid, ErrorBar, PolarAngleAxis, PolarGrid, Radar, RadarChart,
  RadialBar, RadialBarChart, XAxis, YAxis,
} from 'recharts';

import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { useDimensionLabel, useNumber, useOptionalT, usePercent, useT } from '@/lib/i18n';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { usePersonaName } from '@/hooks/use-persona-name';
import { useBandLabel } from '@/features/result/labels';

/**
 * THE SECOND DATA-DRIVEN CORE (build/05 §"The two data-driven cores").
 *
 * `VIZ` is a `Record<VizHint, FC>` and `ResultView` renders
 * `VIZ[envelope.visualization]`. There is no `if (model === 'RIASEC')` anywhere
 * in the console — PRD FR-R1 requires exactly that, and it is why
 * `visualization` had to be added to the contract in Phase 2.
 *
 * EVERY CHART HERE DRAWS THE `display` PROJECTION, NOT THE TYPED BLOCK. That is
 * contract fix C1: `display` is the render-agnostic `{key,label,value,band}[]`
 * carried by every score, so a consumer can draw a model it has never seen. A
 * radar that read `score.dimensions.R` would work for RIASEC and break on the
 * fourth model; reading `display` cannot.
 *
 * COLOUR (dataviz skill, step 2). Every chart here is SINGLE-SERIES — one
 * candidate's profile across dimensions of one measure — so no categorical
 * palette is involved and no adjacent-pair CVD validation applies: there are no
 * pairs. One hue, `--chart-1`, taken from the design system unchanged so the
 * charts re-skin with the console's eight presets and dark mode rather than
 * pinning hexes that would be right in one theme and wrong in seven.
 *
 * Band is a STATUS, and status is never colour alone (skill non-negotiable): the
 * band label is always rendered as text beside the value.
 */

export interface DisplayDimension {
  key: string;
  label: { en: string; ar: string };
  value: number;
  band: string;
  /** N3 — 0..100 against `VizProps.normGroup`. Absent means NO norm covered it. */
  percentile?: number;
  /**
   * N4 — an interval around `value`, in the same display units as `value`, that
   * names its CONSTRUCTION (`basis`) rather than a coverage level it cannot
   * claim. Never called a confidence interval on screen: that word is a coverage
   * claim, and a reader assuming 95% over-reads a standard-error band twofold.
   */
  interval?: { low: number; high: number; basis: string };
}

export interface VizProps {
  headline: string;
  dimensions: DisplayDimension[];
  /** N3 — the persona whose norm table produced the percentiles, if any did. */
  normGroup?: string;
}

/* --------------------------------------------------- the reference group (N3) */

/**
 * "High compared to whom?" — the first question anyone asks of a score, and the
 * one a counsellor has to be able to answer out loud.
 *
 * A percentile with no cohort named is the same non-answer as no percentile at
 * all, so the two are rendered together or not at all. `normGroup` is a persona
 * id (`ScoringConfig.norms` is keyed by persona), which is a database key and
 * not a cohort a person recognises — `usePersonaName` is what turns it into the
 * name the operator already reads on every other screen.
 *
 * Returns `null` when no dimension carries a percentile, which is every result
 * from a module with no norm table: **nothing is rendered in its place**
 * (decision D-PP2). Calibrating a norm is deferred by PRD §6.5; a screen that
 * said "not available" would be reporting the deferral as a finding about the
 * candidate.
 */
function useNormGroupNote(): (dimensions: DisplayDimension[], normGroup?: string) => string | null {
  const t = useT();
  const personaName = usePersonaName();

  return (dimensions, normGroup) => {
    if (!dimensions.some((d) => d.percentile !== undefined)) return null;
    // The server omits `normGroup` unless a table actually produced something,
    // so the unnamed branch is a stack older than N3 rather than a lie waiting
    // to happen. Say what is true in each case and claim nothing extra.
    return normGroup
      ? t('result.percentile.cohort', { cohort: personaName(normGroup) })
      : t('result.percentile.cohortUnnamed');
  };
}

/* ------------------------------------------------------- the interval (N4) */

/**
 * The interval as recharts' `ErrorBar` wants it: distance BELOW and ABOVE the
 * value, not the endpoints.
 *
 * Exported because it is the whole encoding — a test can assert the arithmetic
 * without a browser, which is the only way to assert it at all: recharts
 * measures its container, and jsdom has no layout, so the chart renders nothing
 * in a test. Clamped at zero so an interval that does not bracket its own value
 * cannot draw a whisker pointing the wrong way.
 *
 * **`undefined` when there is no interval, never `[0, 0]`** — recharts skips a
 * falsy error value but would happily draw a zero-width cap, and a tick mark
 * that says "measured exactly" is worse than no mark at all.
 */
export function intervalOffsets(dimension: DisplayDimension): [number, number] | undefined {
  const { interval, value } = dimension;
  if (!interval) return undefined;
  return [Math.max(0, value - interval.low), Math.max(0, interval.high - value)];
}

/**
 * What to CALL the interval — from `basis`, with the usual three steps down.
 *
 * The band has to be legible as the thing it is. `basis` names the construction
 * (`standard_error`), and the console owns the words: a column header and a
 * sentence, both bilingual, both looked up as `result.interval.*.<basis>`.
 *
 * **A basis this catalogue has never seen names NO basis** — the generic header
 * and a sentence that describes a precision range without saying how it was
 * built. Printing `standard_error` at a reader is the outcome this avoids, and
 * inventing a claim about a construction we do not recognise is the other one.
 * The same three steps `useDimensionLabel` and `useHeadline` take.
 *
 * Mixed bases across one projection collapse to the unnamed form too. One
 * caption cannot honestly name two constructions, and a model that mixes them is
 * a case nobody has designed for yet.
 */
function useIntervalWords(): (dimensions: DisplayDimension[]) => { header: string; hint: string } {
  const t = useT();
  const optionalT = useOptionalT();

  return (dimensions) => {
    const bases = new Set(dimensions.flatMap((d) => (d.interval ? [d.interval.basis] : [])));
    const basis = bases.size === 1 ? [...bases][0] : undefined;

    return {
      header: (basis && optionalT(`result.interval.header.${basis}`)) ?? t('result.interval.header'),
      hint: (basis && optionalT(`result.interval.hint.${basis}`)) ?? t('result.interval.hint'),
    };
  };
}

/**
 * WHICH LANGUAGE A RESULT IS READ IN — and why it is NOT `administeredLocale`.
 *
 * PROGRESS §28c left this as a carry-forward phrased as "the result view still
 * renders English-first, without consulting the envelope's `administeredLocale`
 * — the same shape as the harness bug". H1 makes that the wrong fix, and the
 * distinction is worth stating because the two cases look alike:
 *
 *   - **The harness** shows a CANDIDATE the items. What they are shown is the
 *     administration, so it must follow the instance's pinned locale.
 *   - **This screen** shows an ANALYST a finished envelope. Nothing here is
 *     administered to anyone; it is a report being read. So it follows the
 *     reader's own language, like every other console screen.
 *
 * `administeredLocale` remains visible as a FACT, on the Provenance card, which
 * is what reproducibility metadata is for. Rendering an Arabic-administered
 * result's labels in Arabic to an English-reading analyst would be applying the
 * candidate's language to the reviewer — the mirror image of the §28c defect.
 */
function useChartData(): (dimensions: DisplayDimension[]) => Array<{
  name: string; value: number; band: string; error?: [number, number];
}> {
  const dimensionLabel = useDimensionLabel();
  return (dimensions) =>
    dimensions.map((d) => ({
      name: dimensionLabel(d.key, d.label),
      value: d.value,
      band: d.band,
      error: intervalOffsets(d),
    }));
}

/** Inside a hook, because the series label is now a translated string. */
function useChartConfig(): ChartConfig {
  const t = useT();
  return { value: { label: t('result.score'), color: 'var(--chart-1)' } } satisfies ChartConfig;
}

/* ------------------------------------------------------------------- radar */

/**
 * Six themes on one axis, shape is the point.
 *
 * NO INTERVAL IS DRAWN HERE, and the reason is the form rather than the data: an
 * interval on a radar is a second and third polygon, and three overlapping
 * closed shapes read as three profiles rather than as one profile's precision.
 * The accessible table below carries it, which is this console's standing rule —
 * a chart may not be the only carrier of information.
 */
function RadarViz({ dimensions }: VizProps) {
  const chartData = useChartData();
  const config = useChartConfig();
  return (
    <ChartContainer config={config} className="mx-auto aspect-square max-h-80">
      <RadarChart data={chartData(dimensions)}>
        {/* Recessive grid; the data mark carries the ink. */}
        <PolarGrid stroke="var(--border)" />
        <PolarAngleAxis dataKey="name" tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }} />
        <ChartTooltip content={<ChartTooltipContent />} />
        <Radar
          dataKey="value"
          stroke="var(--color-value)"
          fill="var(--color-value)"
          fillOpacity={0.25}
          strokeWidth={2}
          dot={{ r: 4 }}
        />
      </RadarChart>
    </ChartContainer>
  );
}

/* -------------------------------------------------------------------- bars */

/**
 * Five traits, magnitude compared across a common 0–100 scale — and the one
 * chart here that can carry an interval honestly (N4).
 *
 * ### What is drawn, and what is emphatically not
 *
 * `dimensions[].interval` and only that. It arrives in DISPLAY UNITS, the same
 * 0–100 scale as `value`, from the estimator that produced `value`.
 * `score.skills[].standardError` is SE(θ) on the logit scale and describes a
 * different estimator entirely; drawing one on the other would invent a
 * relationship between two things — and every number would still look plausible,
 * which is what makes it worth a paragraph. SE(θ) is rendered in the technical
 * read, labelled with its scale.
 *
 * ### A band, never a second number
 *
 * The interval is a whisker on the mark. A point estimate drawn as a solid bar
 * is what invites the over-interpretation N4 exists to prevent, and a numeric
 * "±4" beside the score would invite it again in a different font: it reads as
 * more precision, not less. The number IS available — in the table below, where
 * a reader looking for it is looking at a list of figures already.
 *
 * Absent interval ⇒ the bar as it has always been. `intervalOffsets` returns
 * `undefined`, recharts skips the mark, and the whisker layer is not mounted at
 * all when nothing carries one.
 */
function BarsViz({ dimensions }: VizProps) {
  const chartData = useChartData();
  const config = useChartConfig();
  const anyInterval = dimensions.some((d) => d.interval !== undefined);

  return (
    <ChartContainer config={config} className="max-h-80 w-full">
      <BarChart data={chartData(dimensions)} layout="vertical" margin={{ left: 8, right: 16 }}>
        <CartesianGrid horizontal={false} stroke="var(--border)" />
        <XAxis
          type="number"
          domain={[0, 100]}
          tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }}
        />
        <YAxis
          type="category"
          dataKey="name"
          width={140}
          tickLine={false}
          axisLine={false}
          tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }}
        />
        <ChartTooltip content={<ChartTooltipContent />} />
        {/* 4px rounded data-end, anchored to the baseline. */}
        <Bar dataKey="value" fill="var(--color-value)" radius={[0, 4, 4, 0]} barSize={18}>
          {anyInterval && (
            /* Annotation ink, not the series colour (dataviz: text and
               annotations wear text tokens) — and it has to stay legible where
               it crosses the bar's own fill, which `--muted-foreground` would
               not in every one of the console's eight presets. `direction="x"`
               because the layout is vertical, so the value axis is x. */
            <ErrorBar
              dataKey="error"
              direction="x"
              width={5}
              strokeWidth={1.5}
              stroke="var(--foreground)"
            />
          )}
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}

/* ------------------------------------------------------------------- gauge */

/**
 * One dimension per gauge. Recharts' radial bar needs an explicit 0–100 domain,
 * or it scales each arc to its own maximum and every dimension looks full.
 *
 * **The interval is not drawn on the arc, and that is a limitation rather than a
 * decision to be proud of.** A radial band means either a second concentric ring
 * — which reads as a second measure, not as one measure's precision — or an
 * overlapping sector whose geometry cannot be checked without looking at it, and
 * this console is developed against a remote stack with no local render. So the
 * gauge shows the point estimate, and the "All dimensions" table below it (which
 * is always present under a non-table chart) carries the interval in full. If
 * the radial band is worth building, build it against a rendered page, not a
 * type-checked one.
 */
function GaugeViz({ dimensions, normGroup }: VizProps) {
  const config = useChartConfig();
  const dimensionLabel = useDimensionLabel();
  const bandLabel = useBandLabel();
  const t = useT();
  const percent = usePercent();
  const normGroupNote = useNormGroupNote();
  const note = normGroupNote(dimensions, normGroup);

  return (
    <div className="space-y-3">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {dimensions.map((d) => (
          <div key={d.key} className="rounded-lg border p-4 text-center">
            <ChartContainer config={config} className="mx-auto aspect-square max-h-40">
              <RadialBarChart
                data={[{ name: dimensionLabel(d.key, d.label), value: d.value }]}
                startAngle={210}
                endAngle={-30}
                innerRadius="70%"
                outerRadius="100%"
              >
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} axisLine={false} />
                <RadialBar dataKey="value" fill="var(--color-value)" cornerRadius={4} background />
              </RadialBarChart>
            </ChartContainer>
            {/* The hero number, in text ink rather than the series colour. */}
            <p className="text-2xl font-semibold">{d.value}</p>
            <p className="text-sm font-medium">{dimensionLabel(d.key, d.label)}</p>
            <Badge variant="secondary" className="mt-1">{bandLabel(d.band)}</Badge>
            {/* N3 — beside the band, and ONLY when a norm covered this
                dimension. A dimension the norm table does not reach renders its
                band alone, exactly as it did before N3: no dash, no "not
                available", no greyed zero (decision D-PP2). A missing norm is a
                fact about the module's calibration, not about the candidate. */}
            {d.percentile !== undefined && (
              <p className="text-muted-foreground mt-1 text-xs">
                {t('result.percentile.short', { value: percent(d.percentile) })}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Named ONCE for the whole grid rather than on every card: the cohort is a
          property of the norm table, not of a dimension, and twelve repetitions
          of the same clause is noise a reader learns to skip. */}
      {note && <p className="text-muted-foreground text-xs">{note}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------- table */

/**
 * The universal fallback, and the accessible view of every other chart.
 *
 * Any `display` projection renders here, which is what lets a model with no
 * bespoke chart still be read — and satisfies the skill's "a table view exists"
 * accessibility requirement for the three that do have one.
 */
export function TableViz({ dimensions, normGroup }: VizProps) {
  const t = useT();
  const dimensionLabel = useDimensionLabel();
  const bandLabel = useBandLabel();
  const number = useNumber();
  const percent = usePercent();
  const normGroupNote = useNormGroupNote();
  const intervalWords = useIntervalWords();

  /**
   * A column appears only if the envelope has something to put in it.
   *
   * Not cosmetics: an empty "Percentile" column on every un-normed result would
   * make the absence of a norm table look like a gap in this candidate's data,
   * repeated once per row. Nothing is what a module with no norms should render
   * (D-PP2) — and where SOME dimensions are normed and others are not, the
   * column exists and the un-normed cells are genuinely empty.
   */
  const anyPercentile = dimensions.some((d) => d.percentile !== undefined);
  const anyInterval = dimensions.some((d) => d.interval !== undefined);
  // The column's name and the sentence under the table both come from `basis`,
  // so the band is legible as the thing it is rather than as "an interval".
  const words = intervalWords(dimensions);

  const note = [normGroupNote(dimensions, normGroup), anyInterval ? words.hint : null]
    .filter(Boolean)
    .join(' ');

  return (
    <Table>
      {/* The table is where the chart's marks become readable, so the two things
          a mark cannot say — which cohort, and what the whisker means — are said
          here rather than in a tooltip nobody hovers. */}
      {note && <TableCaption>{note}</TableCaption>}
      <TableHeader>
        <TableRow>
          <TableHead>{t('result.dimension')}</TableHead>
          <TableHead className="w-24">{t('result.score')}</TableHead>
          {anyInterval && <TableHead className="w-36">{words.header}</TableHead>}
          <TableHead className="w-32">{t('result.band')}</TableHead>
          {anyPercentile && <TableHead className="w-28">{t('result.percentile')}</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {dimensions.map((d) => {
          return (
            <TableRow key={d.key}>
              {/* One language, the reader's. The English underneath went with the
                  rule that a VIEW shows the operator's language and only an
                  EDITOR shows the pair — and here it was doubly redundant, since
                  the envelope's `ar` is a copy of its `en` by design. */}
              <TableCell>{dimensionLabel(d.key, d.label)}</TableCell>
              <TableCell className="font-medium">{d.value}</TableCell>
              {/* N4 — the interval as a RANGE, which is what a table can carry:
                  the chart draws it and this states it. `dir="ltr"` because the
                  cell is two numerals and a dash, the same treatment a version
                  string gets on the Provenance card. */}
              {anyInterval && (
                <TableCell className="text-muted-foreground" dir="ltr">
                  {d.interval &&
                    t('result.interval.range', {
                      low: number(d.interval.low),
                      high: number(d.interval.high),
                    })}
                </TableCell>
              )}
              <TableCell>
                <Badge variant="secondary">{bandLabel(d.band)}</Badge>
              </TableCell>
              {/* N3 — and an un-normed dimension's cell stays EMPTY. Not "—",
                  not "n/a", not 0. */}
              {anyPercentile && (
                <TableCell>
                  {d.percentile !== undefined && percent(d.percentile)}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/** The map. A new viz hint is one entry; nothing else in the console changes. */
export const VIZ: Record<string, React.FC<VizProps>> = {
  radar: RadarViz,
  bars: BarsViz,
  gauge: GaugeViz,
  table: TableViz,
};
